import { useState } from 'react';
import type { SelectProps } from 'antd';
import { Select } from 'antd';
import type { DefaultOptionType } from 'antd/lib/select';
import cn from 'classnames';
import { selectHelpers } from 'utils/helpers/selectHelpers';
import type { TOptions } from 'utils/types/select';

import { useEffectWithoutInitCall } from 'components/utils/hooks/useEffectWithoutInitCall';

import styles from './SingleSelect.module.scss';

interface ISingleSelectProps<T>
    extends Omit<
        SelectProps<T, DefaultOptionType>,
        'value' | 'options' | 'onChange' | 'suffixIcon'
    > {
    options: TOptions<T>;
    value?: T | null;
    isLoading?: boolean;
    allLabel?: string;
    className?: string;
    placeholder?: string;
    onChange?: (value: T | null) => void;
    customIcon?: React.ReactNode;
}

const filterOption = (input: string, option?: DefaultOptionType) =>
    selectHelpers.doesOptionIncludeInput(option, input, 'children');

const SingleSelect = <T extends string | number>(
    props: ISingleSelectProps<T>,
) => {
    const {
        placeholder,
        className,
        options,
        value: valueFromProps,
        onChange,
        isLoading = false,
        customIcon,
        ...restProps
    } = props;

    const [value, setValue] = useState<T | undefined | null>(valueFromProps);
    const [isOpen, setIsOpen] = useState(false);

    useEffectWithoutInitCall(() => {
        if (valueFromProps !== value) {
            setValue(valueFromProps);
        }
    }, [valueFromProps]);

    const handleChange = (newValue: T) => {
        onChange?.(newValue);
        setValue(newValue);
    };

    const handleDropdownVisibleChange = (open: boolean) => {
        setIsOpen(open);
    };

    const animatedCustomIcon = customIcon ? (
        <div
            className={cn(styles.customIconWrapper, {
                [styles.open]: isOpen,
            })}
        >
            {customIcon}
        </div>
    ) : undefined;

    return (
        <Select
            value={value}
            onChange={handleChange}
            className={className}
            placeholder={placeholder}
            loading={isLoading}
            filterOption={filterOption}
            suffixIcon={animatedCustomIcon}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            {...restProps}
        >
            {options.map(({ label, value: optionValue }) => (
                <Select.Option key={optionValue} value={optionValue}>
                    {label}
                </Select.Option>
            ))}
        </Select>
    );
};

export default SingleSelect;
